import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Crown, Check, Loader2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

const PERKS = [
  "Analyses de courriers illimitées",
  "Coffre-fort illimité + vérification IA",
  "Courriers illimités & export PDF",
  "Lecture à voix haute",
  "Explication multilingue",
  "Assistant Impôts complet",
  "Dossier assemblé (courrier + justificatifs)",
];

const FREE = [
  "3 analyses de document / mois",
  "5 documents au coffre-fort",
  "3 courriers / mois",
];

export default function Subscription() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    api.get("/payments/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const checkout = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur de paiement.");
      setLoading(null);
    }
  };

  const restore = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post("/payments/restore");
      if (data.restored) { toast.success("Abonnement restauré !"); refresh(); }
      else toast.info("Aucun abonnement payé à restaurer.");
    } catch {
      toast.error("Échec de la restauration.");
    } finally { setRestoring(false); }
  };

  return (
    <div className="space-y-6 adminia-fade-in max-w-4xl mx-auto">
      <div className="text-center">
        <div className="mx-auto mb-3 h-14 w-14 rounded-2xl bg-brand/10 flex items-center justify-center">
          <Crown className="h-7 w-7 text-brand" />
        </div>
        <h1 className="font-heading font-bold text-3xl">Passez en Premium</h1>
        <p className="text-muted-foreground mt-2">Toutes vos démarches, sans limite.</p>
      </div>

      {user?.is_premium && (
        <Card className="p-5 bg-success/5 border-success/30 text-center">
          <p className="font-medium text-success inline-flex items-center gap-2 justify-center">
            <Check className="h-5 w-5" />
            {user.subscription_status === "trial" ? "Essai Premium actif" : "Vous êtes abonné Premium"}
          </p>
          {user.premium_until && (
            <p className="text-sm text-muted-foreground mt-1">
              Actif jusqu'au {new Date(user.premium_until).toLocaleDateString("fr-FR")}
            </p>
          )}
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        {/* Free */}
        <Card className="p-6">
          <p className="font-heading font-semibold text-lg">Gratuit</p>
          <p className="text-3xl font-bold mt-2">0€</p>
          <p className="text-sm text-muted-foreground mb-4">pour toujours</p>
          <ul className="space-y-2">
            {FREE.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
        </Card>

        {/* Premium plans */}
        {plans.map((p) => {
          const yearly = p.lookup_key.endsWith("yearly");
          return (
            <Card key={p.lookup_key} className={cn("p-6 relative", yearly ? "border-brand ring-1 ring-brand" : "")}>
              {yearly && (
                <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-brand">Économisez 33%</Badge>
              )}
              <p className="font-heading font-semibold text-lg">{p.label}</p>
              <p className="text-3xl font-bold mt-2">
                {p.amount.toFixed(2).replace(".", ",")}€
                <span className="text-base font-normal text-muted-foreground">/{p.interval}</span>
              </p>
              <p className="text-sm text-muted-foreground mb-4">7 jours d'essai offerts</p>
              <ul className="space-y-2 mb-5">
                {PERKS.slice(0, 5).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-success shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                className={cn("w-full rounded-full h-12", yearly ? "bg-brand hover:bg-brand/90" : "")}
                variant={yearly ? "default" : "outline"}
                disabled={loading === p.lookup_key}
                onClick={() => checkout(p.lookup_key)}
                data-testid={`checkout-${p.lookup_key}`}
              >
                {loading === p.lookup_key ? <Loader2 className="h-5 w-5 animate-spin" /> : "Choisir cette offre"}
              </Button>
            </Card>
          );
        })}
      </div>

      <div className="text-center">
        <Button variant="ghost" className="gap-2 text-muted-foreground" onClick={restore} disabled={restoring} data-testid="restore-btn">
          <RefreshCw className={cn("h-4 w-4", restoring && "animate-spin")} /> Restaurer mon achat
        </Button>
      </div>
      <p className="text-xs text-muted-foreground text-center">
        Paiement sécurisé via Stripe. Sans engagement, annulable à tout moment.
        Carte de test : 4242 4242 4242 4242.
      </p>
    </div>
  );
}
